import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link, Navigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Recycle, ShieldCheck, MapPin, Sparkles, Download, Minus, Plus, ShoppingBag } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import BrandStripe from "@/components/site/BrandStripe";
import { findStyle, STYLES, COMMON_SPEC } from "@/data/products";
import { useEnquiry } from "@/context/EnquiryContext";
import useSEO from "@/hooks/useSEO";

const strengthOptions = [
  { value: "+1.0", bg: "var(--ge-beige)",  fg: "var(--ge-navy)" },
  { value: "+1.5", bg: "var(--ge-yellow)", fg: "var(--ge-navy)" },
  { value: "+2.0", bg: "var(--ge-red)",    fg: "#fff" },
  { value: "+2.5", bg: "var(--ge-green)",  fg: "#fff" },
  { value: "+3.0", bg: "var(--ge-navy)",   fg: "#fff" },
];

export default function ProductDetail() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const product = findStyle(slug);

  const initialColour = useMemo(() => {
    if (!product) return 0;
    const c = params.get("colour");
    const idx = product.variants.findIndex((v) => v.slug === c);
    return idx >= 0 ? idx : 0;
  }, [product, params]);

  const [colourIdx, setColourIdx] = useState(initialColour);
  const [galleryIdx, setGalleryIdx] = useState(0);
  const [strengthIdx, setStrengthIdx] = useState(1);
  const [qty, setQty] = useState(1);
  const { add: addToEnquiry, setOpen: setEnquiryOpen } = useEnquiry();

  const active = product?.variants?.[colourIdx];
  useSEO({
    title: product ? `${product.name}${active ? ` in ${active.color}` : ""}` : "Product",
    description: product
      ? `${product.name}: ${product.tagline} ${product.description.slice(0, 140)}`
      : "Explore Good Eyes reading glasses. Eight styles, 30 colourways, colour-coded by strength.",
    path: `/product/${slug || ""}`,
    image: active?.img,
  });

  useEffect(() => { setColourIdx(initialColour); setGalleryIdx(0); }, [initialColour]);
  useEffect(() => { setGalleryIdx(0); }, [colourIdx]);

  const relatedStyles = useMemo(
    () => STYLES.filter((s) => s.slug !== slug).slice(0, 4),
    [slug]
  );

  if (!product) return <Navigate to="/shop" replace />;
  const galleryImages = active.gallery || [active.img];
  const viewLabels = ["Front", "Side", "Angle"];

  return (
    <div className="min-h-screen bg-white text-[color:var(--ge-navy)]" data-testid={`product-page-${product.slug}`}>
      <Nav />

      <main className="pt-32 md:pt-40">
        <div className="ge-container">
          <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-[color:var(--ge-navy)]/60 flex items-center gap-2">
            <Link to="/shop" className="hover:text-[color:var(--ge-red)] inline-flex items-center gap-1">
              <ArrowLeft size={12} /> The Range
            </Link>
            <span>/</span>
            <span className="text-[color:var(--ge-navy)]">{product.name}</span>
            <span>/</span>
            <span className="text-[color:var(--ge-navy)]/70">{active.color}</span>
          </div>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
            {/* LEFT: image gallery */}
            <div className="lg:col-span-7">
              <div className="relative bg-white rounded-2xl border border-[color:var(--ge-navy)]/10 aspect-[4/3] flex items-center justify-center overflow-hidden p-8 md:p-14">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={galleryImages[galleryIdx]}
                    src={galleryImages[galleryIdx]}
                    alt={`${product.name} in ${active.color} (${viewLabels[galleryIdx] || "view"})`}
                    initial={{ opacity: 0, scale: 1.03 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.35 }}
                    className="max-h-[70%] max-w-[70%] w-auto product-img"
                    data-testid="pdp-hero-image"
                  />
                </AnimatePresence>
                <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.28em] font-semibold text-[color:var(--ge-navy)]/60">
                  {product.name} · {active.color}
                </div>
                <div className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.28em] font-semibold text-[color:var(--ge-navy)]/60">
                  {viewLabels[galleryIdx] || "View"} view
                </div>
              </div>

              {/* View thumbnails (front/side/angle) */}
              <div className="mt-4 grid grid-cols-3 gap-3">
                {galleryImages.map((src, i) => (
                  <button
                    key={src}
                    onClick={() => setGalleryIdx(i)}
                    className={`relative bg-white border rounded-xl aspect-[4/3] flex items-center justify-center overflow-hidden p-4 transition-all ${galleryIdx === i ? "border-[color:var(--ge-navy)] shadow-sm" : "border-[color:var(--ge-navy)]/10"}`}
                    data-testid={`pdp-view-${viewLabels[i]?.toLowerCase() || i}`}
                    aria-label={`${viewLabels[i] || "View"} view`}
                  >
                    <img src={src} alt="" className="max-h-[55%] max-w-[70%] w-auto" />
                    <span className="absolute bottom-1 left-2 text-[9px] uppercase tracking-[0.2em] font-semibold text-[color:var(--ge-navy)]/70">
                      {viewLabels[i] || `View ${i + 1}`}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* RIGHT: details */}
            <div className="lg:col-span-5">
              <h1 className="display-font tight-cap font-extrabold text-[color:var(--ge-navy)] leading-[0.95] text-[clamp(2.4rem,5vw,4rem)]">
                {product.name}
              </h1>
              <div className="mt-3 h-[4px] w-14 rounded-full" style={{ background: "var(--ge-yellow)" }} />
              <p className="mt-4 text-lg md:text-xl font-medium text-[color:var(--ge-red)]">
                {product.tagline}
              </p>
              <p className="mt-6 text-base font-light text-[color:var(--ge-navy)]/80 leading-relaxed">
                {product.description}
              </p>

              <div className="mt-8 flex items-baseline gap-2">
                <span className="text-3xl md:text-4xl font-bold text-[color:var(--ge-navy)] tight-cap">$65</span>
                <span className="text-xs uppercase tracking-[0.24em] font-semibold text-[color:var(--ge-navy)]/60">NZD</span>
              </div>

              {/* Colour swatches — now as FRAME IMAGE thumbnails */}
              <div className="mt-8">
                <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-[color:var(--ge-navy)]/70">
                  Colour / <span className="text-[color:var(--ge-navy)]">{active.color}</span>
                </div>
                <div className="mt-3 grid grid-cols-5 gap-2">
                  {product.variants.map((v, i) => (
                    <button
                      key={v.color}
                      onClick={() => setColourIdx(i)}
                      aria-label={`Colour ${v.color}`}
                      data-testid={`pdp-colour-${v.slug}`}
                      className={`relative bg-white rounded-lg aspect-square flex items-center justify-center overflow-hidden border-2 p-3 md:p-4 transition-all ${colourIdx === i ? "border-[color:var(--ge-navy)] shadow" : "border-[color:var(--ge-navy)]/15 hover:border-[color:var(--ge-navy)]/40"}`}
                      title={v.color}
                    >
                      <img src={v.img} alt={v.color} className="max-h-[60%] max-w-[85%] w-auto" />
                    </button>
                  ))}
                </div>
                <div className="mt-3 text-sm text-[color:var(--ge-navy)]/75 font-light">
                  {active.desc}
                </div>
              </div>

              {/* Strength picker */}
              <div className="mt-8">
                <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-[color:var(--ge-navy)]/70">
                  Strength / <span className="text-[color:var(--ge-navy)]">{strengthOptions[strengthIdx].value}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {strengthOptions.map((s, i) => (
                    <button
                      key={s.value}
                      onClick={() => setStrengthIdx(i)}
                      data-testid={`pdp-strength-${s.value.replace(".", "-")}`}
                      className={`h-12 w-16 rounded-full font-bold text-sm transition-transform ${strengthIdx === i ? "scale-105 shadow-sm ring-2 ring-[color:var(--ge-navy)]" : ""}`}
                      style={{ background: s.bg, color: s.fg }}
                    >
                      {s.value}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quantity + Enquire CTA */}
              <div className="mt-10">
                <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-[color:var(--ge-navy)]/70">
                  Quantity
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <div className="inline-flex items-center border border-[color:var(--ge-navy)]/20 rounded-full overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setQty((q) => Math.max(1, q - 1))}
                      aria-label="Decrease quantity"
                      data-testid="pdp-qty-dec"
                      className="h-11 w-11 flex items-center justify-center hover:bg-[color:var(--ge-cream)] transition-colors"
                    >
                      <Minus size={14} />
                    </button>
                    <input
                      type="number"
                      min="1"
                      max="99"
                      value={qty}
                      onChange={(e) => setQty(Math.max(1, Math.min(99, parseInt(e.target.value || 1, 10) || 1)))}
                      data-testid="pdp-qty-input"
                      className="w-12 text-center bg-transparent text-base font-bold text-[color:var(--ge-navy)] focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setQty((q) => Math.min(99, q + 1))}
                      aria-label="Increase quantity"
                      data-testid="pdp-qty-inc"
                      className="h-11 w-11 flex items-center justify-center hover:bg-[color:var(--ge-cream)] transition-colors"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  <button
                    type="button"
                    data-testid="pdp-add-to-enquiry"
                    onClick={() => {
                      addToEnquiry({
                        styleSlug: product.slug,
                        styleName: product.name,
                        colourSlug: active.slug,
                        colourName: active.color,
                        strength: strengthOptions[strengthIdx].value,
                        qty,
                        img: active.img,
                      });
                      toast.success(`${product.name} · ${active.color} · ${strengthOptions[strengthIdx].value} added to enquiry.`);
                      setEnquiryOpen(true);
                    }}
                    className="flex-1 min-w-[220px] inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--ge-navy)] text-white px-7 py-4 text-xs uppercase tracking-[0.24em] font-semibold hover:bg-[color:var(--ge-red)] transition-colors"
                  >
                    <ShoppingBag size={14} /> Add to enquiry
                  </button>
                </div>
                <p className="mt-3 text-[11px] text-[color:var(--ge-navy)]/60 leading-relaxed max-w-md">
                  Not a checkout. Your enquiry is emailed to sales@goodeyes.co.nz and we&apos;ll reply with pricing and availability.
                </p>
                <div className="mt-4">
                  <Link to="/contact" data-testid="pdp-ask-us" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] font-semibold text-[color:var(--ge-navy)] hover:text-[color:var(--ge-red)] transition-colors">
                    Or ask us a question <ArrowRight size={12} />
                  </Link>
                </div>
              </div>

              {/* Size guide link */}
              <a
                href="/assets/good-eyes-size-guide.pdf"
                target="_blank"
                rel="noreferrer"
                data-testid="pdp-size-guide-link"
                className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] font-semibold text-[color:var(--ge-navy)] hover:text-[color:var(--ge-red)] transition-colors"
              >
                <Download size={14} /> Size Guide (PDF)
              </a>

              <ul className="mt-10 grid grid-cols-2 gap-3 text-sm">
                <FeatureRow icon={<Recycle size={16} />} color="var(--ge-green)">Recycled co-polyester</FeatureRow>
                <FeatureRow icon={<ShieldCheck size={16} />} color="var(--ge-yellow)">BPA free</FeatureRow>
                <FeatureRow icon={<MapPin size={16} />} color="var(--ge-navy)">Designed in NZ</FeatureRow>
                <FeatureRow icon={<Sparkles size={16} />} color="var(--ge-beige)" fg="var(--ge-navy)">Recycled felt pouch</FeatureRow>
              </ul>
            </div>
          </div>

          {/* Size & sustainability */}
          <div className="mt-24 grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-7 rounded-2xl border border-[color:var(--ge-navy)]/10 bg-white p-6 md:p-8">
              <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-[color:var(--ge-navy)]/70">
                Size Guide
              </div>
              <div className="mt-1 h-[3px] w-10 rounded-full" style={{ background: "var(--ge-yellow)" }} />
              <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
                <SizeCell label="Frame width" value={product.size.frame} />
                <SizeCell label="Lens width" value={product.size.lens} />
                <SizeCell label="Lens height" value={product.size.lensH} />
                <SizeCell label="Bridge width" value={product.size.bridge} />
                <SizeCell label="Temple length" value={product.size.temple} />
              </div>
              <a href="/assets/good-eyes-size-guide.pdf" target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] font-semibold text-[color:var(--ge-navy)] hover:text-[color:var(--ge-red)] transition-colors">
                <Download size={14} /> Download full size guide
              </a>
            </div>
            <div className="md:col-span-5 rounded-2xl p-6 md:p-8 text-white" style={{ background: "var(--ge-green)" }}>
              <div className="text-[11px] uppercase tracking-[0.28em] font-semibold" style={{ color: "var(--ge-yellow)" }}>
                Sustainability
              </div>
              <div className="mt-1 h-[3px] w-10 rounded-full" style={{ background: "var(--ge-yellow)" }} />
              <p className="mt-4 text-base font-light leading-relaxed text-white/90">
                {COMMON_SPEC}
              </p>
            </div>
          </div>

          <div className="mt-24">
            <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-[color:var(--ge-navy)]/70">
              You may also like
            </div>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              {relatedStyles.map((r) => (
                <Link key={r.slug} to={`/product/${r.slug}`} className="group rounded-2xl border border-[color:var(--ge-navy)]/10 bg-white p-4 flex flex-col items-center transition-colors hover:border-[color:var(--ge-navy)]/40">
                  <img src={r.variants[0].img} alt={r.name} className="h-24 w-auto" />
                  <div className="mt-3 text-lg font-bold text-[color:var(--ge-navy)] group-hover:text-[color:var(--ge-red)] transition-colors">
                    {r.name}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-[color:var(--ge-navy)]/60 font-semibold">
                    {r.variants.length} colours
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-24">
          <BrandStripe height={22} />
        </div>
      </main>

      <Footer />
    </div>
  );
}

const FeatureRow = ({ icon, color, fg = "#fff", children }) => (
  <li className="flex items-center gap-3 text-[color:var(--ge-navy)]/80">
    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full" style={{ background: color, color: fg }}>
      {icon}
    </span>
    <span className="font-medium">{children}</span>
  </li>
);

const SizeCell = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--ge-navy)]/60">
      {label}
    </div>
    <div className="mt-1 text-lg font-bold text-[color:var(--ge-navy)]">{value}</div>
  </div>
);
